import React, { useState, useEffect } from 'react';
import apiClient from '../../api/apiClient';
import apiService from '../../api/apiService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CloudQueue as ApiIcon,
  Input as InputIcon,
  AccountTree as BranchIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import BranchConditionBuilder from './BranchConditionBuilder';
import VisualFlowEditor from './VisualFlowEditor';
import VariableManager from './VariableManager';
import MultiLanguageInput from './MultiLanguageInput';

// Sortable Step Component
function SortableStep({ 
  step, 
  index, 
  onRemove, 
  onEdit, 
  expandedStep, 
  setExpandedStep, 
  getAvailableVariablesForStep, 
  availableApiEndpoints,
  getSelectedApiEndpoint,
  getResponseFieldsForEndpoint,
  updateParameterMapping,
  updateResponseMapping,
  flowData,
  projectId,
  canManage = true,
  hasReadOnlyAccess = false
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Safety check for step object
  if (!step || !step.type || !step.id) {
    console.warn('Invalid step object:', step);
    return null;
  }

  // Get step icon based on type
  const getStepIcon = (stepType) => {
    switch (stepType) {
      case 'api_call':
        return <ApiIcon className="w-5 h-5" />;
      case 'message':
        return <MessageIcon className="w-5 h-5" />;
      case 'collect_input':
        return <InputIcon className="w-5 h-5" />;
      case 'branch':
        return <BranchIcon className="w-5 h-5" />;
      default:
        return <SettingsIcon className="w-5 h-5" />;
    }
  };

  // Get step color based on type
  const getStepColor = (stepType) => {
    switch (stepType) {
      case 'api_call':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'message':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'collect_input':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'branch':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  // Format step type for display
  const formatStepType = (stepType) => {
    if (!stepType || typeof stepType !== 'string') {
      return 'Unknown Step';
    }
    return stepType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg transition-all duration-200 ${
        isDragging 
          ? 'shadow-lg transform rotate-3 border-blue-300 opacity-50' 
          : 'shadow-sm hover:shadow-md border-gray-200'
      } ${getStepColor(step.type)}`}
    >
      {/* Step Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center flex-1">
          {canManage && (
            <div 
              {...attributes} 
              {...listeners}
              className="mr-3 cursor-grab active:cursor-grabbing"
            >
              <DragIcon className="w-5 h-5 text-gray-400" />
            </div>
          )}
          <div className="flex items-center mr-3">
            {getStepIcon(step.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-white rounded-full text-xs font-medium mr-2">
                {index + 1}
              </span>
              <h4 className="font-medium">{step.name || 'Unnamed Step'}</h4>
            </div>
            <p className="text-sm opacity-75 mt-1">
              {formatStepType(step.type)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
            className="text-gray-600 hover:text-gray-800 p-1 rounded bg-dark-gray-custom mr-2"
            title={hasReadOnlyAccess ? "View step configuration" : "Configure step"}
          >
            <EditIcon className="w-4 h-4" />
          </button>
          {canManage && (
            <button
              onClick={() => onRemove(step.id)}
              className="text-red-600 hover:text-red-800 p-1 rounded bg-red-custom"
              title="Delete step"
            >
              <DeleteIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Step Configuration (Expanded) */}
      {expandedStep === step.id && (
        <div className="border-t border-opacity-20 p-4 bg-white bg-opacity-50">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Step Name
              </label>
              <input
                type="text"
                value={step.name || ''}
                onChange={(e) => onEdit(step.id, { name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            
            {/* API Call Configuration */}
            {step.type === 'api_call' && (
              <div className="space-y-6">
                {/* API Endpoint Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Endpoint *
                  </label>
                  <select
                    value={(() => {
                      const endpointValue = step.config?.apiCall?.apiEndpoint || '';
                      console.log('🎯 FlowEditor DROPDOWN VALUE DEBUG:');
                      console.log('  - step.config?.apiCall?.apiEndpoint:', step.config?.apiCall?.apiEndpoint);
                      console.log('  - Final dropdown value:', endpointValue);
                      console.log('  - Available endpoints:', availableApiEndpoints.map(ep => ({ id: ep._id, name: ep.name })));
                      return endpointValue;
                    })()}
                    onChange={(e) => onEdit(step.id, {
                      config: {
                        ...step.config,
                        apiCall: {
                          ...step.config?.apiCall,
                          apiEndpoint: e.target.value,
                          // Reset mappings when endpoint changes
                          parameterMapping: {},
                          parameters: {}
                        }
                      },
                      // Reset response mapping when endpoint changes
                      variableMapping: {
                        ...step.variableMapping,
                        saveFromResponse: {}
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Select API Endpoint</option>
                    {availableApiEndpoints.map((endpoint) => (
                      <option key={endpoint._id} value={endpoint._id}>
                        {endpoint.name} ({endpoint.method} {endpoint.url})
                      </option>
                    ))}
                  </select>
                  {availableApiEndpoints.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No API endpoints found. Create API endpoints first in the API Manager.
                    </p>
                  )}
                </div>

                {/* Show endpoint details if selected */}
                {step.config?.apiCall?.apiEndpoint && (() => {
                  const selectedEndpoint = getSelectedApiEndpoint(step.config.apiCall.apiEndpoint);
                  if (!selectedEndpoint) return null;

                  return (
                    <>
                      {/* Endpoint Details */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">Endpoint Details</h4>
                        <div className="text-xs text-blue-800 space-y-1">
                          <div><strong>Method:</strong> {selectedEndpoint.method}</div>
                          <div><strong>URL:</strong> {selectedEndpoint.url}</div>
                          {selectedEndpoint.description && (
                            <div><strong>Description:</strong> {selectedEndpoint.description}</div>
                          )}
                        </div>
                      </div>

                      {/* Parameter Mapping - Simplified */}
                      {selectedEndpoint.parameters && Array.isArray(selectedEndpoint.parameters) && selectedEndpoint.parameters.length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                            <SettingsIcon className="w-4 h-4 mr-2 text-blue-500" />
                            API Parameters
                          </h4>
                          <p className="text-xs text-gray-600 mb-3">
                            Configure the parameters to send to the API. You can use static values or placeholders like {'{user_message}'}, {'{user_name}'}, etc.
                          </p>
                          {/* Parameters Summary */}
                          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <h5 className="text-sm font-medium text-blue-900 mb-2">Parameters Summary</h5>
                            <div className="space-y-1 text-xs">
                              {(selectedEndpoint.parameters || []).map((paramConfig, index) => {
                                const paramKey = paramConfig.name;
                                const currentParam = step.config?.apiCall?.parameters?.[paramKey];
                                const isConfigured = currentParam && currentParam.type && (
                                  currentParam.value || 
                                  currentParam.variableName || 
                                  currentParam.extractPrompt
                                );
                                return (
                                  <div key={`summary-${paramKey}-${index}`} className="flex items-center justify-between">
                                    <span className="text-blue-800 font-medium">{paramKey}</span>
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      isConfigured 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {isConfigured ? 'Configured' : 'Needs Setup'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            {(() => {
                              console.log('🔍 LIST VIEW PARAMETER DEBUG:');
                              console.log('  - selectedEndpoint.parameters:', selectedEndpoint.parameters);
                              console.log('  - typeof selectedEndpoint.parameters:', typeof selectedEndpoint.parameters);
                              console.log('  - Array.isArray(selectedEndpoint.parameters):', Array.isArray(selectedEndpoint.parameters));
                              console.log('  - step.config?.apiCall?.parameters:', step.config?.apiCall?.parameters);
                              return null;
                            })()}
                            {(selectedEndpoint.parameters || []).map((paramConfig, index) => {
                              const paramKey = paramConfig.name;
                              return (
                              <div key={paramKey} className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{paramKey}</span>
                                    {paramConfig.required && (
                                      <span className="ml-1 text-xs text-red-600">*</span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                    {paramConfig.type || 'string'}
                                  </span>
                                </div>
                                
                                {paramConfig.description && (
                                  <p className="text-xs text-gray-600 mb-2">{paramConfig.description}</p>
                                )}

                                <div className="space-y-2">
                                  {/* Parameter Value Type Selection */}
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Parameter Source:</label>
                                    <select
                                      value={step.config?.apiCall?.parameters?.[paramKey]?.type || 'static'}
                                      onChange={(e) => {
                                        const sourceType = e.target.value;
                                        onEdit(step.id, {
                                          config: {
                                            ...step.config,
                                            apiCall: {
                                              ...step.config?.apiCall,
                                              parameters: {
                                                ...step.config?.apiCall?.parameters,
                                                [paramKey]: {
                                                  type: sourceType,
                                                  value: '',
                                                  extractPrompt: sourceType === 'ai_extract' ? `Extract the ${paramKey} from the user message` : '',
                                                  variableName: sourceType === 'previous_variable' ? '' : undefined
                                                }
                                              }
                                            }
                                          }
                                        });
                                      }}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="static">Static Value</option>
                                      <option value="previous_variable">Variable from Previous Step</option>
                                      <option value="user_message">Full User Message</option>
                                      <option value="ai_extract">AI Extract from Message</option>
                                      <option value="user_data">User Data (name, email, etc.)</option>
                                    </select>
                                  </div>

                                  {/* Parameter Value Input */}
                                  {(() => {
                                    const paramType = step.config?.apiCall?.parameters?.[paramKey]?.type || 'static';
                                    const paramValue = step.config?.apiCall?.parameters?.[paramKey]?.value || '';
                                    const paramVariableName = step.config?.apiCall?.parameters?.[paramKey]?.variableName || '';
                                    
                                    switch (paramType) {
                                      case 'static':
                                        return (
                                          <input
                                            type="text"
                                            value={paramValue}
                                            onChange={(e) => {
                                              onEdit(step.id, {
                                                config: {
                                                  ...step.config,
                                                  apiCall: {
                                                    ...step.config?.apiCall,
                                                    parameters: {
                                                      ...step.config?.apiCall?.parameters,
                                                      [paramKey]: {
                                                        ...step.config?.apiCall?.parameters?.[paramKey],
                                                        value: e.target.value
                                                      }
                                                    }
                                                  }
                                                }
                                              });
                                            }}
                                            placeholder={paramConfig.example || `Enter ${paramKey} value`}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                          />
                                        );
                                      
                                      case 'previous_variable':
                                        const availableVars = getAvailableVariablesForStep(step.order - 1);
                                        return (
                                          <div className="space-y-2">
                                            <select
                                              value={paramVariableName}
                                              onChange={(e) => {
                                                onEdit(step.id, {
                                                  config: {
                                                    ...step.config,
                                                    apiCall: {
                                                      ...step.config?.apiCall,
                                                      parameters: {
                                                        ...step.config?.apiCall?.parameters,
                                                        [paramKey]: {
                                                          ...step.config?.apiCall?.parameters?.[paramKey],
                                                          variableName: e.target.value,
                                                          value: `{{${e.target.value}}}`
                                                        }
                                                      }
                                                    }
                                                  }
                                                });
                                              }}
                                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            >
                                              <option value="">Select a variable...</option>
                                              {availableVars.map((variable) => (
                                                <option key={variable.name} value={variable.name}>
                                                  {variable.name} ({variable.type}) - from {variable.source}
                                                </option>
                                              ))}
                                            </select>
                                            {availableVars.length === 0 && (
                                              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                                <p className="text-xs text-yellow-800">
                                                  <strong>No variables available:</strong> Add steps before this one that save variables to use them here.
                                                </p>
                                              </div>
                                            )}
                                            {paramVariableName && (
                                              <div className="bg-green-50 p-3 rounded border border-green-200">
                                                <p className="text-xs text-green-800">
                                                  <strong>Will send:</strong> The value of variable "{paramVariableName}" from a previous step
                                                </p>
                                                <p className="text-xs text-green-600 mt-1">
                                                  Preview: {`{{${paramVariableName}}}`}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      
                                      case 'user_message':
                                        return (
                                          <div className="bg-blue-50 p-3 rounded border">
                                            <p className="text-xs text-blue-800 mb-2">
                                              <strong>Will send:</strong> The complete user message as-is
                                            </p>
                                            <p className="text-xs text-blue-600">
                                              Example: If user says "Saya lagi cari dokter penyakit dalam", 
                                              the entire message will be sent to the API.
                                            </p>
                                          </div>
                                        );
                                      
                                      case 'ai_extract':
                                        return (
                                          <div className="space-y-2">
                                            <textarea
                                              value={step.config?.apiCall?.parameters?.[paramKey]?.extractPrompt || ''}
                                              onChange={(e) => {
                                                onEdit(step.id, {
                                                  config: {
                                                    ...step.config,
                                                    apiCall: {
                                                      ...step.config?.apiCall,
                                                      parameters: {
                                                        ...step.config?.apiCall?.parameters,
                                                        [paramKey]: {
                                                          ...step.config?.apiCall?.parameters?.[paramKey],
                                                          extractPrompt: e.target.value
                                                        }
                                                      }
                                                    }
                                                  }
                                                });
                                              }}
                                              placeholder={`Extract the ${paramKey} from the user message`}
                                              rows={2}
                                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            <div className="bg-green-50 p-3 rounded border">
                                              <p className="text-xs text-green-800 mb-2">
                                                <strong>AI Extraction:</strong> Uses AI to extract specific information
                                              </p>
                                              <p className="text-xs text-green-600 mb-2">
                                                <strong>Example prompt:</strong> "Extract the medical specialty name from the user message"
                                              </p>
                                              <p className="text-xs text-green-600">
                                                Input: "Saya lagi cari dokter penyakit dalam" → Output: "penyakit dalam"
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      
                                      case 'user_data':
                                        return (
                                          <div className="space-y-2">
                                            <select
                                              value={paramValue}
                                              onChange={(e) => {
                                                onEdit(step.id, {
                                                  config: {
                                                    ...step.config,
                                                    apiCall: {
                                                      ...step.config?.apiCall,
                                                      parameters: {
                                                        ...step.config?.apiCall?.parameters,
                                                        [paramKey]: {
                                                          ...step.config?.apiCall?.parameters?.[paramKey],
                                                          value: e.target.value
                                                        }
                                                      }
                                                    }
                                                  }
                                                });
                                              }}
                                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            >
                                              <option value="">Select user data field</option>
                                              <option value="user_name">User Name</option>
                                              <option value="user_email">User Email</option>
                                              <option value="user_phone">User Phone</option>
                                              <option value="user_id">User ID</option>
                                              <option value="session_id">Session ID</option>
                                              <option value="user_location">User Location</option>
                                              <option value="user_language">User Language</option>
                                              <option value="user_timezone">User Timezone</option>
                                            </select>
                                            <div className="bg-purple-50 p-3 rounded border">
                                              <p className="text-xs text-purple-800">
                                                <strong>User Data:</strong> Pulls information about the current user
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      
                                      default:
                                        return null;
                                    }
                                  })()}
                                </div>
                              </div>
                            )})}
                          </div>
                        </div>
                      )}

                      {/* Response Mapping - Enhanced with Multiple Fields */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <SaveIcon className="w-4 h-4 mr-2 text-green-500" />
                          Save Response Data
                        </h4>
                        <p className="text-xs text-gray-600 mb-3">
                          Choose which data from the API response to save for use in later steps.
                        </p>

                        <div className="space-y-4">
                          {/* Save entire response */}
                          <div className="bg-green-50 rounded-lg p-3">
                            <label className="block text-sm font-medium text-green-900 mb-2">
                              Save entire response as variable:
                            </label>
                            <input
                              type="text"
                              value={step.variableMapping?.saveFullResponse || ''}
                              onChange={(e) => {
                                onEdit(step.id, {
                                  variableMapping: {
                                    ...step.variableMapping,
                                    saveFullResponse: e.target.value
                                  }
                                });
                              }}
                              placeholder="Enter variable name (e.g., api_response)"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-green-600 mt-1">
                              This will save the complete API response data that you can use in later steps.
                            </p>
                          </div>

                          {/* Multiple Response Field Mappings */}
                          <div className="bg-blue-50 rounded-lg p-3">
                            <label className="block text-sm font-medium text-blue-900 mb-2">
                              Extract specific fields:
                            </label>
                            
                            {/* List of current mappings */}
                            <div className="space-y-2 mb-3">
                              {(step.variableMapping?.responseFields || []).map((mapping, index) => (
                                <div key={index} className="flex items-center space-x-2 p-2 bg-white rounded border">
                                  <div className="flex-1">
                                    <span className="text-xs text-gray-600">Field:</span>
                                    <div className="font-mono text-sm">{mapping.field}</div>
                                  </div>
                                  <div className="flex-1">
                                    <span className="text-xs text-gray-600">Variable:</span>
                                    <div className="font-medium text-sm">{mapping.variableName}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedFields = [...(step.variableMapping?.responseFields || [])];
                                      updatedFields.splice(index, 1);
                                      onEdit(step.id, {
                                        variableMapping: {
                                          ...step.variableMapping,
                                          responseFields: updatedFields
                                        }
                                      });
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <CloseIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>

                            {/* Add new response field mapping */}
                            <div className="border border-gray-200 rounded p-3 bg-white">
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Response Field Path:
                                  </label>
                                  {selectedEndpoint && getResponseFieldsForEndpoint(selectedEndpoint).length > 0 ? (
                                    <select
                                      value={step._tempMapping?.field || ''}
                                      onChange={(e) => {
                                        onEdit(step.id, {
                                          _tempMapping: {
                                            ...step._tempMapping,
                                            field: e.target.value
                                          }
                                        });
                                      }}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="">Select a response field...</option>
                                      {getResponseFieldsForEndpoint(selectedEndpoint).map((field) => (
                                        <option key={field.path} value={field.path}>
                                          {field.name} ({field.path}) - {field.type}
                                        </option>
                                      ))}
                                      <option value="custom">Custom field path...</option>
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={step._tempMapping?.field || ''}
                                      onChange={(e) => {
                                        onEdit(step.id, {
                                          _tempMapping: {
                                            ...step._tempMapping,
                                            field: e.target.value
                                          }
                                        });
                                      }}
                                      placeholder="data.results or data.user.name"
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  )}
                                  
                                  {/* Custom field input when "custom" is selected */}
                                  {step._tempMapping?.field === 'custom' && (
                                    <input
                                      type="text"
                                      value={step._tempMapping?.customField || ''}
                                      onChange={(e) => {
                                        onEdit(step.id, {
                                          _tempMapping: {
                                            ...step._tempMapping,
                                            customField: e.target.value
                                          }
                                        });
                                      }}
                                      placeholder="Enter custom field path (e.g., data.custom.field)"
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 mt-2"
                                    />
                                  )}
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Save as Variable:
                                  </label>
                                  <input
                                    type="text"
                                    value={step._tempMapping?.variableName || ''}
                                    onChange={(e) => {
                                      onEdit(step.id, {
                                        _tempMapping: {
                                          ...step._tempMapping,
                                          variableName: e.target.value
                                        }
                                      });
                                    }}
                                    placeholder="variable_name"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                                
                                <button
                                  type="button"
                                  onClick={() => {
                                    const field = step._tempMapping?.field === 'custom' ? 
                                      step._tempMapping?.customField : 
                                      step._tempMapping?.field;
                                    const variableName = step._tempMapping?.variableName;
                                    
                                    if (field && variableName) {
                                      const currentFields = step.variableMapping?.responseFields || [];
                                      const newMapping = { field, variableName };
                                      
                                      onEdit(step.id, {
                                        variableMapping: {
                                          ...step.variableMapping,
                                          responseFields: [...currentFields, newMapping]
                                        },
                                        _tempMapping: {} // Clear temp mapping
                                      });
                                    }
                                  }}
                                  disabled={!step._tempMapping?.variableName || 
                                    (!step._tempMapping?.field || 
                                     (step._tempMapping?.field === 'custom' && !step._tempMapping?.customField))}
                                  className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                  Add Response Field Mapping
                                </button>
                              </div>
                            </div>
                            
                            <p className="text-xs text-blue-600 mt-2">
                              Extract specific fields from the API response. For example, if the API returns 
                              {'{data: {user: {name: "John"}}}'}, use "data.user.name" to extract just the name.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Delay Configuration */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <ClockIcon className="w-4 h-4 mr-2 text-yellow-500" />
                          Execution Delay
                        </h4>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={step.config?.apiCall?.delay || 0}
                            onChange={(e) => onEdit(step.id, {
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  delay: parseInt(e.target.value) || 0
                                }
                              }
                            })}
                            className="w-20 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-sm text-gray-600">seconds (0-30)</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Add a delay before executing this step (useful for rate limiting or user experience).
                        </p>
                      </div>

                      {/* Parameter Validation Summary */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <CheckCircleIcon className="w-4 h-4 mr-2 text-green-500" />
                          Configuration Status
                        </h4>
                        {(() => {
                          const allParams = Object.entries(selectedEndpoint.parameters);
                          const configuredParams = allParams.filter(([paramKey, paramConfig]) => {
                            const currentParam = step.config?.apiCall?.parameters?.[paramKey];
                            return currentParam && currentParam.type && (
                              currentParam.value || 
                              currentParam.variableName || 
                              currentParam.extractPrompt
                            );
                          });
                          const missingRequired = allParams.filter(([paramKey, paramConfig]) => {
                            if (!paramConfig.required) return false;
                            const currentParam = step.config?.apiCall?.parameters?.[paramKey];
                            return !currentParam || !currentParam.type || (
                              !currentParam.value && 
                              !currentParam.variableName && 
                              !currentParam.extractPrompt
                            );
                          });

                          return (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="bg-blue-50 p-3 rounded">
                                  <div className="font-medium text-blue-900">Total Parameters</div>
                                  <div className="text-2xl font-bold text-blue-700">{allParams.length}</div>
                                </div>
                                <div className="bg-green-50 p-3 rounded">
                                  <div className="font-medium text-green-900">Configured</div>
                                  <div className="text-2xl font-bold text-green-700">{configuredParams.length}</div>
                                </div>
                              </div>

                              {missingRequired.length > 0 && (
                                <div className="bg-red-50 p-3 rounded border border-red-200">
                                  <p className="text-sm text-red-800 font-medium mb-2">
                                    ⚠️ Missing Required Parameters:
                                  </p>
                                  <div className="space-y-1">
                                    {missingRequired.map(([paramKey, paramConfig]) => (
                                      <div key={paramKey} className="text-xs text-red-700 flex items-center justify-between">
                                        <span className="font-medium">{paramKey}</span>
                                        <span className="text-red-600 bg-red-100 px-2 py-1 rounded">Required</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {missingRequired.length === 0 && allParams.length > 0 && (
                                <div className="bg-green-50 p-3 rounded border border-green-200">
                                  <p className="text-sm text-green-800">
                                    ✅ All required parameters are configured
                                  </p>
                                </div>
                              )}

                              {allParams.length === 0 && (
                                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                  <p className="text-sm text-gray-600">
                                    ℹ️ This API endpoint has no parameters
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* Message Configuration */}
            {step.type === 'message' && (
              <div>
                                  <MultiLanguageInput
                    label="Message Content"
                    content={step.config?.message?.content}
                    onChange={(content) => onEdit(step.id, {
                      config: {
                        ...step.config,
                        message: {
                          ...step.config?.message,
                          content
                        }
                      }
                    })}
                    placeholder="Enter the message to send to the user..."
                    multiline={true}
                    rows={4}
                    useDefaultLanguage={true} // Use "default" language system instead of specific language
                  />
              </div>
            )}
            
            {/* Collect Input Configuration */}
            {step.type === 'collect_input' && (
              <div className="space-y-4">
                <div>
                  <MultiLanguageInput
                    label="Message to User"
                    content={step.config?.collectInput?.message?.content || step.config?.collectInput?.prompt}
                    onChange={(content) => {
                      // Extract primary language content for legacy prompt field
                      let legacyPrompt = '';
                      if (typeof content === 'string') {
                        legacyPrompt = content;
                      } else if (content && typeof content === 'object') {
                        // Priority: default > en (English) > id (Indonesian) > first available
                        legacyPrompt = content.default || content.en || content.id || Object.values(content)[0] || '';
                      }
                      
                      onEdit(step.id, {
                        config: {
                          ...step.config,
                          collectInput: {
                            ...step.config?.collectInput,
                            message: {
                              ...step.config?.collectInput?.message,
                              content
                            },
                            // Keep legacy prompt for backward compatibility
                            prompt: legacyPrompt
                          }
                        }
                      });
                    }}
                    placeholder="Enter the message to show to the user when asking for input..."
                    multiline={true}
                    rows={3}
                    useDefaultLanguage={true} // Use "default" language system instead of specific language
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`required-${step.id}`}
                    checked={step.config?.collectInput?.validation?.required !== false}
                    onChange={(e) => onEdit(step.id, {
                      config: {
                        ...step.config,
                        collectInput: {
                          ...step.config?.collectInput,
                          validation: {
                            ...step.config?.collectInput?.validation,
                            required: e.target.checked
                          }
                        }
                      }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`required-${step.id}`} className="ml-2 block text-sm text-gray-700">
                    Required field
                  </label>
                </div>

                {/* Variable Extraction Configuration */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Variable Extraction from User Input
                  </h4>
                  <VariableManager
                    step={step}
                    onUpdateStep={(updatedStep) => onEdit(step.id, updatedStep)}
                    availableVariables={getAvailableVariablesForStep(index)}
                    projectId={projectId}
                    apiEndpoints={availableApiEndpoints}
                  />
                </div>
              </div>
            )}
            
            {/* Branch Configuration */}
            {step.type === 'branch' && (
              <div className="space-y-6">
                {/* Branch Logic Overview */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-orange-900 mb-2 flex items-center">
                    <BranchIcon className="w-4 h-4 mr-2 text-orange-600" />
                    Branch Logic Configuration
                  </h4>
                  <p className="text-sm text-orange-800 mb-3">
                    Configure conditions to control the flow path. Each condition can redirect to a specific step when met.
                  </p>
                  
                  {/* Flow Diagram */}
                  <div className="bg-white rounded-lg p-3 border border-orange-200">
                    <div className="text-xs text-orange-700 space-y-2">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                        <span><strong>IF</strong> conditions are met → Go to specified step</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                        <span><strong>ELSE</strong> (no conditions met) → Go to default step</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conditions Configuration */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-medium text-gray-900">Conditional Paths</h5>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {(step.config?.branch?.conditions || []).length} condition(s)
                    </span>
                  </div>
                  
                  <BranchConditionBuilder
                    conditions={step.config?.branch?.conditions || []}
                    onConditionsChange={(conditions) => onEdit(step.id, {
                            config: {
                              ...step.config,
                              branch: {
                                ...step.config?.branch,
                                conditions: conditions
                              }
                            }
                    })}
                    availableVariables={getAvailableVariablesForStep(step.order - 1)}
                    availableSteps={flowData.steps.filter(s => s.id !== step.id).map(s => ({ 
                      id: s.id, 
                      name: s.name, 
                      type: s.type 
                    }))}
                    className="mt-4"
                  />
                </div>
                
                {/* Default Next Step */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="w-3 h-3 bg-gray-400 rounded-full mr-3"></div>
                    <h5 className="text-sm font-medium text-gray-900">Default Path</h5>
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What happens if <strong>NO</strong> conditions are met?
                  </label>
                  <select
                    value={step.config?.branch?.defaultNextStepId || ''}
                    onChange={(e) => onEdit(step.id, {
                          config: {
                            ...step.config,
                            branch: {
                              ...step.config?.branch,
                          defaultNextStepId: e.target.value
                        }
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Continue to next step in order</option>
                    {flowData.steps
                      .filter(s => s.id !== step.id)
                      .map((nextStep) => (
                        <option key={nextStep.id} value={nextStep.id}>
                          {nextStep.name} ({nextStep.type})
                        </option>
                      ))}
                  </select>
                  <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs text-blue-800">
                      <strong>Default behavior:</strong> If you don't select a specific step, the flow will continue to the next step in the sequence.
                      This is the fallback path when none of your conditions match.
                    </p>
                  </div>
                </div>

                {/* Logic Operator for Multiple Conditions */}
                {(step.config?.branch?.conditions || []).length > 1 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-yellow-900 mb-2">Multiple Conditions Logic</h5>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`logic-${step.id}`}
                          value="AND"
                          checked={(step.config?.branch?.globalLogicOperator || 'AND') === 'AND'}
                          onChange={(e) => onEdit(step.id, {
                            config: {
                              ...step.config,
                              branch: {
                                ...step.config?.branch,
                                globalLogicOperator: e.target.value
                              }
                            }
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm text-yellow-800">
                          <strong>AND</strong> - All conditions must be true
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`logic-${step.id}`}
                          value="OR"
                          checked={(step.config?.branch?.globalLogicOperator || 'AND') === 'OR'}
                          onChange={(e) => onEdit(step.id, {
                            config: {
                              ...step.config,
                              branch: {
                                ...step.config?.branch,
                                globalLogicOperator: e.target.value
                              }
                            }
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm text-yellow-800">
                          <strong>OR</strong> - Any condition can be true
                        </span>
                      </label>
                </div>
                  </div>
                )}

                {/* Branch Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Branch Summary</h5>
                  <div className="text-sm text-blue-800 space-y-1">
                    {(step.config?.branch?.conditions || []).length === 0 ? (
                      <div>No conditions defined - flow will continue to default step</div>
                    ) : (
                      <>
                        <div><strong>Conditions:</strong> {(step.config?.branch?.conditions || []).length}</div>
                        <div><strong>Logic:</strong> {step.config?.branch?.globalLogicOperator || 'AND'}</div>
                        <div><strong>Default step:</strong> {
                          step.config?.branch?.defaultNextStepId ? 
                            flowData.steps.find(s => s.id === step.config.branch.defaultNextStepId)?.name || 'Unknown step' :
                            'Next step in order'
                        }</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Variable Mapping Section */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Variable Mapping</h4>
              <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded">
                <strong>Available Variables:</strong><br/>
                {getAvailableVariablesForStep(step.order - 1).map(variable => 
                  `{{${variable.name}}} (${variable.type})`
                ).join(', ') || 'None available'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FlowEditor = ({ flow, projectId, onClose, onSave, canManage = true, hasReadOnlyAccess = false }) => {
  const [flowData, setFlowData] = useState({
    name: '',
    description: '',
    active: true,
    linkedIntents: [],
    steps: []
  });
  
  const [availableIntents, setAvailableIntents] = useState([]);
  const [availableApiEndpoints, setAvailableApiEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedStep, setExpandedStep] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'list'

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag and drop handler
  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFlowData((prev) => {
        const oldIndex = prev.steps.findIndex(step => step.id === active.id);
        const newIndex = prev.steps.findIndex(step => step.id === over.id);
        
        const reorderedSteps = arrayMove(prev.steps, oldIndex, newIndex);
        
        // Update order numbers
        const updatedSteps = reorderedSteps.map((step, index) => ({
          ...step,
          order: index + 1
        }));

        return {
          ...prev,
          steps: updatedSteps
        };
      });
    }
  }

  // Load data on component mount
  useEffect(() => {
    if (projectId) {
      fetchAvailableIntents();
      fetchApiEndpoints();
    }
    
    if (flow) {
      // Ensure steps have required properties and convert backend format to frontend format
      const validatedSteps = (flow.steps || [])
        .filter(step => step && (step.id || step._id)) // Keep steps that have some form of ID
        .map(step => {
          // Convert backend format to frontend format
          const config = step.config || {};
          
          // Convert direct field access to config structure
          if (step.branch && !config.branch) {
            config.branch = step.branch;
          }
          if (step.apiCall && !config.apiCall) {
            config.apiCall = {
              ...step.apiCall,
              // Extract just the endpoint ID if it's an object, otherwise keep as is
              apiEndpoint: step.apiCall.apiEndpoint && typeof step.apiCall.apiEndpoint === 'object' ? 
                step.apiCall.apiEndpoint._id || step.apiCall.apiEndpoint.id :
                step.apiCall.apiEndpoint,
              // Ensure parameters object exists and is properly formatted
              parameters: step.apiCall.parameters || {}
            };
            console.log('🔧 FlowEditor API ENDPOINT CONVERSION:');
            console.log('  - Original step.apiCall.apiEndpoint:', step.apiCall.apiEndpoint);
            console.log('  - Converted config.apiCall.apiEndpoint:', config.apiCall.apiEndpoint);
          }
          if (step.collectInput && !config.collectInput) {
            config.collectInput = step.collectInput;
          }
          if (step.message && !config.message) {
            config.message = step.message;
          }
          // 🔧 CRITICAL FIX: Convert Knowledge Lookup from top level to config structure for frontend
          if (step.knowledgeLookup && !config.knowledgeLookup) {
            config.knowledgeLookup = step.knowledgeLookup;
            console.log('🔧 FlowEditor KNOWLEDGE LOOKUP LOAD: Converted knowledgeLookup from top level to config for frontend:', config.knowledgeLookup);
          }
          // CRITICAL FIX: Always copy generateMessage from database when it exists (initial load)
          if (step.generateMessage) {
            config.generateMessage = step.generateMessage;
            console.log(`FlowEditor INITIAL: Copied generateMessage for step ${step.name}:`, {
              from: step.generateMessage,
              to: config.generateMessage
            });
          }
          
          return {
          id: step.id || step._id || `step_${Date.now()}_${Math.random()}`,
          type: step.type || 'message', // Default to message type
          name: step.name || 'Unnamed Step',
          order: step.order || 0,
          active: step.active !== false,
            config: config,
            variableMapping: step.variableMapping || {},
            variableOperations: step.variableOperations || [], // 🔧 CRITICAL FIX: Preserve variableOperations
            position: step.position || { x: 250, y: 200 },
            nextStepId: step.nextStepId // Preserve nextStepId field for connections!
          };
        });

      setFlowData({
        name: flow.name || '',
        description: flow.description || '',
        active: flow.active !== false,
        linkedIntents: flow.linkedIntents?.map(intent => 
          typeof intent === 'object' ? intent._id : intent
        ) || [],
        steps: validatedSteps
      });
      
      console.log(`[FlowEditor] Loaded flow with ${validatedSteps.length} validated steps`);
    }
  }, [flow, projectId]);

  const fetchAvailableIntents = async () => {
    try {
      console.log(`[FlowEditor] Fetching intents for project: ${projectId}`);
      const response = await apiClient.get(`/flow-builder/projects/${projectId}/intents`);
      console.log(`[FlowEditor] Intents API response:`, response);
      
      // Extract intents from the nested data structure
      const intents = response?.data?.intents || [];
      console.log(`[FlowEditor] Extracted ${intents.length} intents`);
      
      setAvailableIntents(Array.isArray(intents) ? intents : []);
    } catch (error) {
      console.error('Error fetching intents:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please refresh the page and try again.');
      } else {
        setError('Failed to load intents. Please try refreshing the page.');
      }
      setAvailableIntents([]);
    }
  };

  const fetchApiEndpoints = async () => {
    try {
      console.log(`[FlowEditor] Fetching API endpoints for project: ${projectId}`);
      const endpoints = await apiService.getApiEndpoints(projectId);
      console.log(`[FlowEditor] API endpoints response:`, endpoints);
      
      console.log(`[FlowEditor] Extracted ${endpoints.length} API endpoints`);
      
      setAvailableApiEndpoints(Array.isArray(endpoints) ? endpoints : []);
    } catch (error) {
      console.error('Error fetching API endpoints:', error);
      if (error.response?.status === 401) {
        setError('Authentication required. Please refresh the page and try again.');
      } else {
        setError(`Failed to load API endpoints: ${error.message}`);
      }
      setAvailableApiEndpoints([]);
    }
  };

  // Get selected API endpoint details
  const getSelectedApiEndpoint = (endpointId) => {
    return availableApiEndpoints.find(endpoint => endpoint._id === endpointId) || null;
  };

  // Get available response fields for mapping
  const getResponseFieldsForEndpoint = (endpoint) => {
    if (!endpoint || !endpoint.responseMapping) return [];
    
    const fields = [];
    
    // Parse response mapping to extract available fields
    Object.entries(endpoint.responseMapping).forEach(([key, mapping]) => {
      if (mapping.path) {
        fields.push({
          key,
          path: mapping.path,
          description: mapping.description || `Response field: ${key}`,
          type: mapping.type || 'string'
        });
      }
    });
    
    return fields;
  };

  // Handle parameter mapping update
  const updateParameterMapping = (stepId, paramKey, variableSource) => {
    updateStep(stepId, {
      config: {
        ...flowData.steps.find(s => s.id === stepId)?.config,
        apiCall: {
          ...flowData.steps.find(s => s.id === stepId)?.config?.apiCall,
          parameterMapping: {
            ...flowData.steps.find(s => s.id === stepId)?.config?.apiCall?.parameterMapping,
            [paramKey]: variableSource
          }
        }
      }
    });
  };

  // Handle response mapping update  
  const updateResponseMapping = (stepId, responseField, variableName) => {
    const step = flowData.steps.find(s => s.id === stepId);
    updateStep(stepId, {
      variableMapping: {
        ...step?.variableMapping,
        saveFromResponse: {
          ...step?.variableMapping?.saveFromResponse,
          [variableName]: responseField
        }
      }
    });
  };

  const getAvailableVariablesForStep = (stepIndex) => {
    const variables = [];
    
    // Add variables from previous steps
    for (let i = 0; i < stepIndex; i++) {
      const step = flowData.steps[i];
      
      // Handle response variables
      if (step.variableMapping?.saveFromResponse) {
        if (Array.isArray(step.variableMapping.saveFromResponse)) {
          step.variableMapping.saveFromResponse.forEach(responseVar => {
            variables.push({
              name: responseVar.variableName,
              source: `Step ${i + 1}: ${step.name}`,
              type: 'response',
              dataType: responseVar.dataType || 'string',
              description: responseVar.description || `Response variable from ${step.name}`
            });
          });
        } else {
          // Handle legacy object format
          Object.keys(step.variableMapping.saveFromResponse).forEach(varName => {
            variables.push({
              name: varName,
              source: `Step ${i + 1}: ${step.name}`,
              type: 'response',
              dataType: 'string',
              description: `Response variable from ${step.name}`
            });
          });
        }
      }
      
      // Handle input variables
      if (step.variableMapping?.saveFromInput) {
        if (Array.isArray(step.variableMapping.saveFromInput)) {
          step.variableMapping.saveFromInput.forEach(inputVar => {
            variables.push({
              name: inputVar.variableName,
              source: `Step ${i + 1}: ${step.name}`,
              type: 'input',
              dataType: inputVar.inputType || 'string',
              description: inputVar.description || `Input variable from ${step.name}`
            });
          });
        } else {
          // Handle legacy object format
          Object.keys(step.variableMapping.saveFromInput).forEach(varName => {
            variables.push({
              name: varName,
              source: `Step ${i + 1}: ${step.name}`,
              type: 'input',
              dataType: 'string',
              description: `Input variable from ${step.name}`
            });
          });
        }
      }
      
      // 🔧 NEW: Handle variables from Variable Operations
      if (step.variableOperations && Array.isArray(step.variableOperations)) {
        step.variableOperations.forEach(operation => {
          if (operation.variableName && operation.operation !== 'clear') {
            let dataType = 'string';
            let description = `Created by variable operation: ${operation.operation}`;
            
            // Determine variable type based on the operation value
            if (operation.operation === 'set_value' && operation.value) {
              if (operation.value === '[]') {
                dataType = 'array';
                description = 'Empty array created by Variable Operations';
              } else if (operation.value === '{}') {
                dataType = 'object';
                description = 'Empty object created by Variable Operations';
              } else if (operation.value.startsWith('[') && operation.value.endsWith(']')) {
                dataType = 'array';
                description = 'Array variable created by Variable Operations';
              } else if (operation.value.startsWith('{') && operation.value.endsWith('}')) {
                dataType = 'object';
                description = 'Object variable created by Variable Operations';
              } else if (operation.value === 'true' || operation.value === 'false') {
                dataType = 'boolean';
                description = 'Boolean variable created by Variable Operations';
              } else if (!isNaN(operation.value)) {
                dataType = 'number';
                description = 'Number variable created by Variable Operations';
              }
            } else if (operation.operation === 'set_null') {
              dataType = 'null';
              description = 'Variable set to null by Variable Operations';
            }
            
            variables.push({
              name: operation.variableName,
              source: `Step ${i + 1}: ${step.name}`,
              type: 'variable_operation',
              dataType: dataType,
              description: description
            });
          }
        });
      }
      
      // Also check config.variableOperations for backwards compatibility
      if (step.config?.variableOperations && Array.isArray(step.config.variableOperations)) {
        step.config.variableOperations.forEach(operation => {
          if (operation.variableName && operation.operation !== 'clear') {
            // Only add if not already added from top-level variableOperations
            const alreadyAdded = variables.some(v => v.name === operation.variableName && v.type === 'variable_operation');
            if (!alreadyAdded) {
              let dataType = 'string';
              let description = `Created by variable operation: ${operation.operation}`;
              
              // Determine variable type based on the operation value
              if (operation.operation === 'set_value' && operation.value) {
                if (operation.value === '[]') {
                  dataType = 'array';
                  description = 'Empty array created by Variable Operations';
                } else if (operation.value === '{}') {
                  dataType = 'object';
                  description = 'Empty object created by Variable Operations';
                } else if (operation.value.startsWith('[') && operation.value.endsWith(']')) {
                  dataType = 'array';
                  description = 'Array variable created by Variable Operations';
                } else if (operation.value.startsWith('{') && operation.value.endsWith('}')) {
                  dataType = 'object';
                  description = 'Object variable created by Variable Operations';
                } else if (operation.value === 'true' || operation.value === 'false') {
                  dataType = 'boolean';
                  description = 'Boolean variable created by Variable Operations';
                } else if (!isNaN(operation.value)) {
                  dataType = 'number';
                  description = 'Number variable created by Variable Operations';
                }
              } else if (operation.operation === 'set_null') {
                dataType = 'null';
                description = 'Variable set to null by Variable Operations';
              }
              
              variables.push({
                name: operation.variableName,
                source: `Step ${i + 1}: ${step.name}`,
                type: 'variable_operation',
                dataType: dataType,
                description: description
              });
            }
          }
        });
      }
    }
    
    // Add comprehensive system variables
    const systemVariables = [
      { name: 'user_name', description: 'Current user name', dataType: 'string' },
      { name: 'user_email', description: 'Current user email', dataType: 'string' },
      { name: 'user_id', description: 'Current user ID', dataType: 'string' },
      { name: 'session_id', description: 'Current session ID', dataType: 'string' },
      { name: 'current_timestamp', description: 'Current timestamp', dataType: 'string' },
      { name: 'current_date', description: 'Current date', dataType: 'string' },
      { name: 'last_user_message', description: 'Last user message', dataType: 'string' },
      { name: 'user_location', description: 'Extract location information', dataType: 'string' },
      { name: 'medical_specialty', description: 'Medical Speciality', dataType: 'string' }
    ];
    
    systemVariables.forEach(sysVar => {
      variables.push({
        name: sysVar.name,
        source: 'System',
        type: 'system',
        dataType: sysVar.dataType,
        description: sysVar.description
      });
    });
    
    return variables;
  };

  const handleInputChange = (field, value) => {
    setFlowData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addStep = (stepType) => {
    const newStep = {
      id: `step_${Date.now()}`,
      type: stepType,
      name: getDefaultStepName(stepType),
      order: flowData.steps.length + 1,
      active: true,
      config: getDefaultStepConfig(stepType),
      variableMapping: {}
    };
    
    setFlowData(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));
    
    // Auto-expand the new step for editing
    setExpandedStep(newStep.id);
  };

  const getDefaultStepName = (stepType) => {
    const stepCount = flowData.steps.filter(s => s.type === stepType).length;
    const typeNames = {
      message: 'Send Message',
      collect_input: 'Collect Input',
      api_call: 'API Call',
      branch: 'Branch Logic'
    };
    return `${typeNames[stepType] || stepType} ${stepCount + 1}`;
  };

  const getDefaultStepConfig = (stepType) => {
    switch (stepType) {
      case 'message':
        return {
          message: {
            content: 'Enter your message here...'
          }
        };
      case 'collect_input':
        return {
          collectInput: {
            prompt: 'Please provide your input:',
            validation: {
              required: true,
              errorMessage: 'This field is required.'
            }
          }
        };
      case 'api_call':
        return {
          apiCall: {
            apiEndpoint: '',
            parameters: {},
            delay: 0
          }
        };
      case 'branch':
        return {
          branch: {
            conditions: [],
            defaultNextStepId: null
          }
        };
      default:
        return {};
    }
  };

  const removeStep = (stepId) => {
    const updatedSteps = flowData.steps
      .filter(step => step.id !== stepId)
      .map((step, index) => ({
        ...step,
        order: index + 1
      }));
    
    setFlowData(prev => ({
      ...prev,
      steps: updatedSteps
    }));
    
    // Close expanded step if it was the one being removed
    if (expandedStep === stepId) {
      setExpandedStep(null);
    }
  };

  const updateStep = (stepId, updates) => {
    setFlowData(prev => ({
      ...prev,
      steps: prev.steps.map(step => 
        step.id === stepId 
          ? { ...step, ...updates }
          : step
      )
    }));
  };

  const handleSave = async () => {
    if (!flowData.name.trim()) {
      setError('Flow name is required');
      return;
    }
    
    if (!flowData.linkedIntents || flowData.linkedIntents.length === 0) {
      setError('At least one linked intent is required');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('🔒 SAVE: Starting save process...');
      console.log('🔒 SAVE: viewMode:', viewMode);
      console.log('🔒 SAVE: typeof window:', typeof window);
      console.log('🔒 SAVE: window !== undefined:', typeof window !== 'undefined');
      console.log('🔒 SAVE: condition check:', viewMode === 'visual' && typeof window !== 'undefined');
      
      // 🎯 ALWAYS check for configuration changes first, regardless of view mode
      if (typeof window !== 'undefined' && window.applyCurrentConfigChanges) {
        console.log('🔒 SAVE: Found applyCurrentConfigChanges - applying config changes regardless of view mode');
        try {
          const configChangesApplied = window.applyCurrentConfigChanges();
          console.log('🔒 SAVE: Configuration changes applied:', configChangesApplied);
          if (configChangesApplied) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (error) {
          console.error('🔒 SAVE: Error applying config changes:', error);
        }
      }
      
      // If in visual mode, sync positions and edges from the visual editor before saving
      if (viewMode === 'visual' && typeof window !== 'undefined') {
        console.log('🔒 SAVE: Entered visual mode save block');
        // 🎯 CRITICAL: Apply any current changes from open configuration panel FIRST
        console.log('🔒 SAVE: Checking for current configuration changes before save');
        console.log('🔒 SAVE: window.applyCurrentConfigChanges exists:', !!window.applyCurrentConfigChanges);
        
        let configChangesApplied = false;
        console.log('🔒 SAVE: Available window functions:', {
          syncVisualFlowPositions: typeof window.syncVisualFlowPositions,
          syncVisualFlowEdges: typeof window.syncVisualFlowEdges,
          resetInternalChangeFlag: typeof window.resetInternalChangeFlag,
          applyCurrentConfigChanges: typeof window.applyCurrentConfigChanges
        });
        
        if (window.applyCurrentConfigChanges && typeof window.applyCurrentConfigChanges === 'function') {
          console.log('🔒 SAVE: Applying current configuration changes before save');
          try {
            configChangesApplied = window.applyCurrentConfigChanges();
            console.log('🔒 SAVE: Configuration changes applied:', configChangesApplied);
          
            // Give time for the changes to be applied and state to settle
            if (configChangesApplied) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } catch (error) {
            console.error('🔒 SAVE: Error applying current config changes:', error);
          }
        } else {
          console.log('🔒 SAVE: No applyCurrentConfigChanges function available or not a function');
          console.log('🔒 SAVE: window.applyCurrentConfigChanges type:', typeof window.applyCurrentConfigChanges);
          console.log('🔒 SAVE: window.applyCurrentConfigChanges value:', window.applyCurrentConfigChanges);
        }
        
        if (window.syncVisualFlowPositions) {
          console.log('🎯 POSITION SYNC: Syncing visual positions before save');
          window.syncVisualFlowPositions();
        }
        if (window.syncVisualFlowEdges) {
          console.log('🔗 EDGE SYNC: Syncing visual edges before save');
          window.syncVisualFlowEdges();
        }
        
        // Wait for all sync operations to complete
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Ensure the internalChangeRef flag is reset before proceeding
        if (window.resetInternalChangeFlag) {
          window.resetInternalChangeFlag();
        }
      }
      // Clean up temporary fields from steps before saving
      console.log('🔒 SAVE: Cleaning steps before API call');
      console.log('🔒 SAVE: flowData.steps count:', flowData.steps?.length);
      console.log('🔒 SAVE: flowData.steps sample:', flowData.steps?.slice(0, 2));
      
      const cleanedSteps = flowData.steps.map(step => {
        const cleanedStep = { ...step };
        
        // Remove temporary mapping fields
        delete cleanedStep._tempMapping;
        delete cleanedStep._tempCondition;
        delete cleanedStep._tempVariableOperation;
        
        // 🔧 CRITICAL FIX: Clean up empty nextStepId values to prevent MongoDB validation errors
        if (cleanedStep.nextStepId === '' || cleanedStep.nextStepId === null) {
          cleanedStep.nextStepId = null;
        }
        
        // Clean up branch conditions with empty nextStepId values
        if (cleanedStep.type === 'branch' && cleanedStep.config?.branch?.conditions) {
          cleanedStep.config.branch.conditions = cleanedStep.config.branch.conditions.map(condition => ({
            ...condition,
            nextStepId: condition.nextStepId === '' ? null : condition.nextStepId
          }));
          
          // Also clean up branch-level nextStepId fields
          if (cleanedStep.config.branch.trueNextStepId === '') {
            cleanedStep.config.branch.trueNextStepId = null;
          }
          if (cleanedStep.config.branch.falseNextStepId === '') {
            cleanedStep.config.branch.falseNextStepId = null;
          }
        }
        
        // Ensure proper structure for different step types
        if (step.type === 'api_call') {
          // Ensure API call configuration is properly structured
          if (!cleanedStep.config) {
            cleanedStep.config = {};
          }
          if (!cleanedStep.config.apiCall) {
            cleanedStep.config.apiCall = {};
          }
          
          // Clean up parameter mapping
          if (cleanedStep.config.apiCall.parameters) {
            const cleanedParams = {};
            Object.entries(cleanedStep.config.apiCall.parameters).forEach(([key, value]) => {
              if (value && typeof value === 'object' && value.type) {
                cleanedParams[key] = value;
              } else if (value) {
                // Convert old format to new format
                cleanedParams[key] = {
                  type: 'static',
                  value: value
                };
              }
            });
            cleanedStep.config.apiCall.parameters = cleanedParams;
          }
        }
        
        return cleanedStep;
      });
      
      console.log('🔒 SAVE: Steps cleaned, count:', cleanedSteps.length);
      
      const dataToSend = {
        ...flowData,
        steps: cleanedSteps,
        project: projectId
      };
      
      console.log('🎯 POSITION SYNC: Saving flow data:', JSON.stringify(dataToSend, null, 2));
      console.log('🎯 POSITION SYNC: Step positions being saved:', cleanedSteps.map(s => ({ id: s.id, name: s.name, position: s.position })));
      console.log('🔗 EDGE SYNC: Step connections being saved:', cleanedSteps.map(s => ({ 
        id: s.id, 
        name: s.name, 
        nextStepId: s.nextStepId,
        nextStepIdType: typeof s.nextStepId,
        nextStepIdValue: JSON.stringify(s.nextStepId),
        branchConfig: s.config?.branch ? {
          trueNextStepId: s.config.branch.trueNextStepId,
          falseNextStepId: s.config.branch.falseNextStepId
        } : null
      })));
      
      // Debug: Check branch steps specifically
      const branchSteps = cleanedSteps.filter(step => step.type === 'branch');
      if (branchSteps.length > 0) {
        console.log('Branch steps being saved:', branchSteps.map(step => ({
          id: step.id,
          name: step.name,
          branchConfig: step.config?.branch,
          conditions: step.config?.branch?.conditions
        })));
      }
      
      console.log('🔒 SAVE: About to make API call');
      console.log('🔒 SAVE: flow exists:', !!flow);
      console.log('🔒 SAVE: flow._id:', flow?._id);
      console.log('🔒 SAVE: projectId:', projectId);
      console.log('🔒 SAVE: dataToSend keys:', Object.keys(dataToSend));
      console.log('🔒 SAVE: dataToSend.steps count:', dataToSend.steps?.length);
      
      let response;
      if (flow && flow._id) {
        // Update existing flow
        console.log(`🔒 SAVE: [API Request] PUT /flow-builder/projects/${projectId}/flows/${flow._id}`);
        console.log('🔒 SAVE: Request payload:', JSON.stringify(dataToSend, null, 2));
        response = await apiClient.put(`/flow-builder/projects/${projectId}/flows/${flow._id}`, dataToSend);
        console.log(`🔒 SAVE: [API Response] PUT /flow-builder/projects/${projectId}/flows/${flow._id}`, response.data);
      } else {
        // Create new flow
        console.log(`🔒 SAVE: [API Request] POST /flow-builder/projects/${projectId}/flows`);
        console.log('🔒 SAVE: Request payload:', JSON.stringify(dataToSend, null, 2));
        response = await apiClient.post(`/flow-builder/projects/${projectId}/flows`, dataToSend);
        console.log(`🔒 SAVE: [API Response] POST /flow-builder/projects/${projectId}/flows`, response.data);
      }
      
      // Handle multiple response formats
      console.log('🔍 Response debugging:');
      console.log('  - response.status:', response.status);
      console.log('  - response.data:', response.data);
      console.log('  - response.data.status:', response.data.status);
      console.log('  - response.data.flow exists:', !!response.data.flow);
      console.log('  - response.data.data exists:', !!response.data.data);
      console.log('  - response.data.data.flow exists:', !!response.data.data?.flow);
      
      // Multiple possible response formats:
      // 1. {status: "success", data: {flow: ...}}  (expected backend format)
      // 2. {flow: ...}                            (direct flow object)
      // 3. {data: {flow: ...}}                    (nested data)
      const isSuccess = (
        // Check for axios interceptor modified response where status is "success" string
        response.status === 'success' ||
        // Backend sends success status in data
        (response.data && response.data.status === 'success') ||
        // HTTP success with flow data (numeric status codes)
        ((response.status === 200 || response.status === 201) && 
         (response.data.flow || response.data.data?.flow)) ||
        // Any response with flow data and good HTTP status
        (response.data && (response.status >= 200 && response.status < 300) && 
         (response.data.flow || response.data.data?.flow)) ||
        // Simple case: just check if we have flow data
        (response.data && response.data.flow)
      );
      
      console.log('🔍 isSuccess evaluation:', isSuccess);
      
      if (isSuccess) {
        console.log('Flow saved successfully:', response.data);
        console.log('Response data structure:', {
          hasData: !!response.data.data,
          hasFlow: !!response.data.flow,
          dataFlow: response.data.data?.flow ? 'exists' : 'missing',
          directFlow: response.data.flow ? 'exists' : 'missing'
        });
        
        // Refresh the flow data to show the updated information
        // Handle multiple response formats to extract the flow object
        const savedFlow = response.data.data?.flow || response.data.flow || response.data;
        console.log('🔍 Extracted savedFlow:', savedFlow);
        if (savedFlow) {
          console.log(`[FlowEditor] Refreshing flow data with ${savedFlow.steps?.length || 0} steps`);
          console.log('[FlowEditor] Raw savedFlow:', savedFlow);
          console.log('[FlowEditor] SavedFlow steps:', savedFlow.steps);
          
          // Update the flow data with the response from the server
          const refreshedSteps = (savedFlow.steps || [])
            .filter(step => step && (step.id || step._id))
            .map(step => {
              // Convert backend format to frontend format
              const config = step.config || {};
              
              // Convert direct field access to config structure
              if (step.branch && !config.branch) {
                config.branch = step.branch;
              }
              if (step.apiCall && !config.apiCall) {
                config.apiCall = step.apiCall;
              }
              if (step.collectInput && !config.collectInput) {
                config.collectInput = step.collectInput;
              }
              if (step.message && !config.message) {
                config.message = step.message;
              }
              // 🔧 CRITICAL FIX: Convert Knowledge Lookup from top level to config structure for frontend
              if (step.knowledgeLookup && !config.knowledgeLookup) {
                config.knowledgeLookup = step.knowledgeLookup;
                console.log('🔧 FlowEditor REFRESH KNOWLEDGE LOOKUP LOAD: Converted knowledgeLookup from top level to config for frontend:', config.knowledgeLookup);
              }
              // CRITICAL FIX: Always copy generateMessage from database when it exists
              if (step.generateMessage) {
                config.generateMessage = step.generateMessage;
                console.log(`FlowEditor: Copied generateMessage for step ${step.name}:`, {
                  from: step.generateMessage,
                  to: config.generateMessage
                });
              }
              
              return {
              id: step.id || step._id || `step_${Date.now()}_${Math.random()}`,
              type: step.type || 'message',
              name: step.name || 'Unnamed Step',
              order: step.order || 0,
              active: step.active !== false,
                config: config,
                variableMapping: step.variableMapping || {},
                variableOperations: step.variableOperations || [], // 🔧 CRITICAL FIX: Preserve variableOperations
                position: step.position || { x: 250, y: 200 },
                nextStepId: step.nextStepId // Preserve nextStepId for connections!
              };
            });
          
          setFlowData({
            name: savedFlow.name || '',
            description: savedFlow.description || '',
            active: savedFlow.active !== false,
            linkedIntents: savedFlow.linkedIntents?.map(intent => 
              typeof intent === 'object' ? intent._id : intent
            ) || [],
            steps: refreshedSteps
          });
          
          console.log(`[FlowEditor] Flow data refreshed with ${refreshedSteps.length} steps`);
        }
        
        // Call the onSave callback if provided
        if (onSave) {
          onSave(savedFlow);
        }
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Flow saved but response format was unexpected. Please refresh the page.');
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.data?.message) {
        setError(`Failed to save flow: ${error.response.data.message}`);
      } else if (error.response?.status === 404) {
        setError('API endpoint not found. Please check your server configuration.');
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please refresh the page and try again.');
      } else {
        setError(`Failed to save flow: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const stepTypes = [
    {
      type: 'message',
      name: 'Send Message',
      description: 'Send a message to the user',
      icon: <MessageIcon className="w-6 h-6" />,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      type: 'collect_input',
      name: 'Collect Input',
      description: 'Ask user for input and save it',
      icon: <InputIcon className="w-6 h-6" />,
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      type: 'api_call',
      name: 'API Call',
      description: 'Make an external API call',
      icon: <ApiIcon className="w-6 h-6" />,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      type: 'branch',
      name: 'Branch Logic',
      description: 'Create conditional branching',
      icon: <BranchIcon className="w-6 h-6" />,
      color: 'bg-orange-500 hover:bg-orange-600'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-xl">
          <div>
            <h2 className="text-2xl font-bold">
              {flow ? 'Edit Flow' : 'Create New Flow'}
            </h2>
            <p className="text-blue-100 mt-1">
              Design your conversational flow with drag-and-drop steps
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex bg-blue-400 rounded-lg p-1">
              <button
                onClick={() => setViewMode('visual')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  viewMode === 'visual' 
                    ? 'bg-white text-blue-600' 
                    : 'text-blue-100 hover:text-white'
                }`}
              >
                Visual
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-blue-600' 
                    : 'text-blue-100 hover:text-white'
                }`}
              >
                List
              </button>
            </div>
            
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {viewMode === 'visual' ? (
            /* Visual Flow Editor */
            <div className="w-full">
              <VisualFlowEditor
                flowData={flowData}
                onFlowDataChange={setFlowData}
                availableVariables={getAvailableVariablesForStep(flowData.steps.length)}
                availableApiEndpoints={availableApiEndpoints}
                availableIntents={availableIntents}
                availableSteps={flowData.steps.map(step => ({
                  id: step.id,
                  name: step.name,
                  type: step.type
                }))}
                projectId={projectId}
              />
            </div>
          ) : (
            /* List View Editor */
            <>
          {/* Left Panel - Flow Configuration */}
          <div className="w-1/3 border-r border-gray-200 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <ErrorIcon className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-red-800 font-medium">Error</h4>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <InfoIcon className="w-5 h-5 mr-2 text-blue-500" />
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flow Name *
                    </label>
                    <input
                      type="text"
                      value={flowData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter flow name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={flowData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe what this flow does"
                    />
                  </div>
                  
                  {/* Flow Status Toggle */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-base font-medium text-gray-900 mb-1">Flow Status</h4>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${!flowData.active ? 'text-gray-600' : 'text-gray-500'}`}>
                              Inactive
                            </span>
                            
                            {/* Toggle Switch */}
                            <button
                              onClick={() => handleInputChange('active', !flowData.active)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                flowData.active ? 'bg-green-600' : 'bg-gray-300'
                              } cursor-pointer`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  flowData.active ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            
                            <span className={`text-sm font-medium ${flowData.active ? 'text-green-600' : 'text-gray-500'}`}>
                              Active
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mt-2">
                          {flowData.active ? (
                            <span>
                              <strong>Active:</strong> This flow can be triggered by user messages matching its linked intents.
                            </span>
                          ) : (
                            <span>
                              <strong>Inactive:</strong> This flow is disabled and will not be triggered, even if intents match.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Linked Intents */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500" />
                  Linked Intents ({availableIntents.length} available)
                </h3>
                <div className="space-y-3">
                  {Array.isArray(availableIntents) && availableIntents.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel id="linked-intents-label">Select Intents</InputLabel>
                      <Select
                        labelId="linked-intents-label"
                        multiple
                        value={flowData.linkedIntents}
                        onChange={(e) => handleInputChange('linkedIntents', e.target.value)}
                        label="Select Intents"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((intentId) => {
                              const intent = availableIntents.find(i => i._id === intentId);
                              return (
                                <Chip
                                  key={intentId}
                                  label={intent?.name || 'Unknown Intent'}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    '& .MuiChip-deleteIcon': {
                                      color: '#1976d2'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 300,
                            },
                          },
                        }}
                      >
                        {availableIntents.map((intent) => (
                          <MenuItem key={intent._id} value={intent._id}>
                            <div className="flex flex-col w-full">
                              <div className="font-medium text-gray-900">{intent.name}</div>
                              {intent.description && (
                                <div className="text-xs text-gray-500 mt-1">{intent.description}</div>
                              )}
                              {intent.utterances && intent.utterances.length > 0 && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Examples: {intent.utterances.slice(0, 2).join(', ')}
                                  {intent.utterances.length > 2 && '...'}
                                </div>
                              )}
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <InfoIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">
                        {availableIntents.length === 0 
                          ? "No intents available. Create intents first to link them to this flow."
                          : "Loading intents..."
                        }
                      </p>
                    </div>
                  )}
                  
                  {/* Display selected intents summary */}
                  {flowData.linkedIntents.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-medium text-blue-800 mb-2">
                        Selected Intents ({flowData.linkedIntents.length}):
                      </div>
                      <div className="space-y-1">
                        {flowData.linkedIntents.map((intentId) => {
                          const intent = availableIntents.find(i => i._id === intentId);
                          return intent ? (
                            <div key={intentId} className="text-xs text-blue-700">
                              • {intent.name}
                              {intent.priority && (
                                <span className="ml-2 px-1.5 py-0.5 bg-blue-200 text-blue-800 rounded text-xs">
                                  Priority: {intent.priority}
                                </span>
                              )}
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add Step Buttons */}
              {canManage && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AddIcon className="w-5 h-5 mr-2 text-blue-500" />
                    Add Flow Steps
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {stepTypes.map((stepType) => (
                      <button
                        key={stepType.type}
                        onClick={() => addStep(stepType.type)}
                        className={`${stepType.color} text-white p-4 rounded-lg hover:shadow-md transition-all duration-200 transform hover:scale-105`}
                      >
                        <div className="flex items-center justify-center mb-2">
                          {stepType.icon}
                        </div>
                        <div className="text-sm font-medium">{stepType.name}</div>
                        <div className="text-xs opacity-90 mt-1">{stepType.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Flow Steps */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Flow Steps ({flowData.steps.filter(step => step.type !== 'start' && step.type !== 'end').length})
              </h3>
              <p className="text-gray-600 text-sm">
                Drag and drop to reorder steps. Click on a step to configure it.
              </p>
            </div>

            {flowData.steps.filter(step => step.type !== 'start' && step.type !== 'end').length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <AddIcon className="w-12 h-12 text-gray-400" />
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No steps yet</h4>
                <p className="text-gray-600 mb-4">Add your first step using the buttons on the left</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={flowData.steps}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {flowData.steps
                      .filter(step => step && step.id && step.type && step.type !== 'start' && step.type !== 'end') // Filter out invalid steps and start/end nodes
                      .map((step, index) => (
                        <SortableStep
                          key={step.id}
                          step={step}
                          index={index}
                          onRemove={removeStep}
                          onEdit={updateStep}
                          expandedStep={expandedStep}
                          setExpandedStep={setExpandedStep}
                          getAvailableVariablesForStep={getAvailableVariablesForStep}
                          availableApiEndpoints={availableApiEndpoints}
                          getSelectedApiEndpoint={getSelectedApiEndpoint}
                          getResponseFieldsForEndpoint={getResponseFieldsForEndpoint}
                          updateParameterMapping={updateParameterMapping}
                          updateResponseMapping={updateResponseMapping}
                          flowData={flowData}
                          projectId={projectId}
                          canManage={canManage}
                          hasReadOnlyAccess={hasReadOnlyAccess}
                        />
                      ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {flowData.steps.filter(step => step.type !== 'start' && step.type !== 'end').length} steps • {flowData.linkedIntents.length} linked intents
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-red-custom"
              >
                {hasReadOnlyAccess ? 'Close' : 'Cancel'}
              </button>
              {canManage && (
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center bg-dark-gray-custom"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon className="w-4 h-4 mr-2" />
                      Save Flow
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowEditor; 